import { useIntervalEffect } from "@react-hookz/web";
import type YouTube from "@vip30/react-youtube";
import { useCallback, useRef, useState } from "react";
import PlayerStates from "youtube-player/dist/constants/PlayerStates";
import type { YouTubePlayer } from "youtube-player/dist/types";

const PROGRESS_POLL_INTERVAL_MS = 250;

type ProgressSnapshot = {
  currentTime: number;
  duration: number;
  loadedFraction: number;
};

const EMPTY_SNAPSHOT: ProgressSnapshot = {
  currentTime: 0,
  duration: 0,
  loadedFraction: 0,
};

/** Player interaction logic for the chromeless click-to-play flow: the
 * native YouTube controls are hidden (controls=0), so play/pause, mute and
 * seeking are driven through the player instance captured on the
 * react-youtube ref. The progress poll only runs while playing —
 * onPlaybackStarted seeds isPlaying because the poll itself derives it
 * (otherwise the two would deadlock and the bar never moves).
 */
export const useVideoPlayerControls = () => {
  const playerReference = useRef<YouTube>(null);
  const [hasPlaybackStarted, setHasPlaybackStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState<ProgressSnapshot>(EMPTY_SNAPSHOT);

  const getPlayer = useCallback(
    () => playerReference.current?.internalPlayer as undefined | YouTubePlayer,
    [],
  );

  const handlePlaybackStarted = useCallback(() => {
    setHasPlaybackStarted(true);
    setIsPlaying(true);
  }, []);

  const handleToggleMute = useCallback(() => {
    const player = getPlayer();
    if (!player) return;

    const toggle = async () => {
      if (await player.isMuted()) {
        await player.unMute();
        setIsMuted(false);
      } else {
        await player.mute();
        setIsMuted(true);
      }
    };

    void toggle();
  }, [getPlayer]);

  const handleSeek = useCallback(
    (seconds: number) => {
      const player = getPlayer();
      if (!player) return;

      void player.seekTo(seconds, true);
      setProgress(state => ({ ...state, currentTime: seconds }));
    },
    [getPlayer],
  );

  useIntervalEffect(
    () => {
      const player = getPlayer();
      if (!player) return;

      void (async () => {
        const [currentTime, duration, loadedFraction] = await Promise.all([
          Promise.resolve(player.getCurrentTime()),
          Promise.resolve(player.getDuration()),
          Promise.resolve(player.getVideoLoadedFraction()),
        ]);
        const isCurrentlyPlaying = (await player.getPlayerState()) === PlayerStates.PLAYING;

        setIsPlaying(isCurrentlyPlaying);
        setProgress({ currentTime, duration, loadedFraction });
      })();
    },
    hasPlaybackStarted && isPlaying ? PROGRESS_POLL_INTERVAL_MS : undefined,
  );

  return {
    handlePlaybackStarted,
    handleSeek,
    handleToggleMute,
    hasPlaybackStarted,
    isMuted,
    playerReference,
    progress,
  };
};